import React, { useState, useEffect, useCallback, useRef } from "react";
import HeaderLogo from "../../src/assets/media/logo.png";
import { AiFillHome } from "react-icons/ai";
import { BiLogIn, BiSolidContact } from "react-icons/bi";
import { FaBriefcase, FaMountainSun, FaSuitcaseRolling } from "react-icons/fa6";
import { RiInformation2Fill } from "react-icons/ri";
import { FiLogIn, FiMenu } from "react-icons/fi";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { ImCross } from "react-icons/im";
import Login from "../pages/Login";
import ProfileAPI from "../api/ProfileAPI";

const Header = () => {
  const { id } = useParams();
  const [loginToggle, setLoginToggle] = useState(false);
  const [serviceToggle, setServiceToggle] = useState(false);
  const [toggleOffCanvas, setToggleOffCanvas] = useState(false);
  const [user, setUser] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileProfileDropdown, setShowMobileProfileDropdown] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [profileImg, setProfileImg] = useState(null);
  const [profileImgLoading, setProfileImgLoading] = useState(false);

  const location = useLocation();
  const dropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const profileRef = useRef(null);
  const mobileProfileRef = useRef(null);
  const navigate = useNavigate();

  // Initialize user from localStorage
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && parsedUser.name) {
          setUser(parsedUser);
        } else {
          localStorage.removeItem("user");
        }
      }
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
      localStorage.removeItem("user");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch profile image when user changes - Fixed to use id from useParams
  useEffect(() => {
    const fetchProfileData = async () => {
      if (user && id && !profileImgLoading) {
        setProfileImgLoading(true);
        try {
          const res = await ProfileAPI.postProfile(id);
          if (res?.data?.image) {
            setProfileImg(res.data);
          }
        } catch (error) {
          console.error("Error fetching profile data:", error);
        } finally {
          setProfileImgLoading(false);
        }
      } else if (!user) {
        setProfileImg(null);
        setProfileImgLoading(false);
      }
    };

    fetchProfileData();
  }, [user, id]); // Depend on both user and id

  // Handle body overflow for mobile menu
  useEffect(() => {
    if (toggleOffCanvas) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [toggleOffCanvas]);

  // Close dropdown when clicking outside - Desktop
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileRef.current &&
        !profileRef.current.contains(event.target)
      ) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showProfileDropdown]);

  // Close mobile dropdown when clicking outside - Mobile
  useEffect(() => {
    const handleMobileClickOutside = (event) => {
      if (
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(event.target) &&
        mobileProfileRef.current &&
        !mobileProfileRef.current.contains(event.target)
      ) {
        setShowMobileProfileDropdown(false);
      }
    };

    if (showMobileProfileDropdown) {
      document.addEventListener("mousedown", handleMobileClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleMobileClickOutside);
    }
  }, [showMobileProfileDropdown]);

  // Close dropdowns when location changes
  useEffect(() => {
    setShowProfileDropdown(false);
    setShowMobileProfileDropdown(false);
    setServiceToggle(false);
    setToggleOffCanvas(false);
    setLoginToggle(false);
  }, [location.pathname]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const handleLogoClick = useCallback(() => {
    if (location.pathname === "/") {
      scrollToTop();
    }
  }, [location.pathname, scrollToTop]);

  const handleToggleOffCanvasMenu = useCallback(() => {
    setToggleOffCanvas((prev) => !prev);
    setShowProfileDropdown(false);
    setShowMobileProfileDropdown(false);
  }, []);

  const handleLoginToggle = useCallback((e) => {
    e.preventDefault();
    setLoginToggle((prev) => !prev);
    setShowProfileDropdown(false);
    setShowMobileProfileDropdown(false);
  }, []);

  const handleServiceToggle = useCallback(() => {
    setServiceToggle((prev) => !prev);
  }, []);

  const handleLoginSuccess = useCallback((userData) => {
    try {
      if (userData && userData.name) {
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        setLoginToggle(false);
        setShowProfileDropdown(false);
        setShowMobileProfileDropdown(false);
      } else {
        console.error("Invalid user data received");
      }
    } catch (error) {
      console.error("Error saving user data:", error);
    }
  }, []);

  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem("user");
      setUser(null);
      setProfileImg(null);
      setShowProfileDropdown(false);
      setShowMobileProfileDropdown(false);
      setLoginToggle(false);
      setToggleOffCanvas(false);

      navigate("/");

      setTimeout(() => {
        setLoginToggle(true);
      }, 100);
    } catch (error) {
      console.error("Error during logout:", error);
    }
  }, [navigate]);

  const toggleProfileDropdown = useCallback((e) => {
    e.stopPropagation();
    setShowProfileDropdown((prev) => !prev);
  }, []);

  const toggleMobileProfileDropdown = useCallback((e) => {
    e.stopPropagation();
    setShowMobileProfileDropdown((prev) => !prev);
  }, []);

  const ProfileDropdown = ({ isMobile = false }) => {
    if (!user) return null;

    const isOpen = isMobile ? showMobileProfileDropdown : showProfileDropdown;
    const toggleFunction = isMobile
      ? toggleMobileProfileDropdown
      : toggleProfileDropdown;
    const ref = isMobile ? mobileProfileRef : profileRef;
    const dropdownRefToUse = isMobile ? mobileDropdownRef : dropdownRef;

    return (
      <div
        ref={ref}
        style={{ position: "relative", marginLeft: isMobile ? 10 : 0 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            color: "#fff",
            padding: "4px 8px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onClick={toggleFunction}
          onMouseEnter={(e) => {
            if (!isMobile) {
              e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isMobile) {
              e.currentTarget.style.backgroundColor = "transparent";
            }
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              marginRight: 8,
              border: "2px solid rgba(255,255,255,0.3)",
              overflow: "hidden",
              backgroundColor: "#ddd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {profileImgLoading ? (
              <div style={{ fontSize: "10px", color: "#666" }}>...</div>
            ) : profileImg?.image ? (
              <img
                src={profileImg.image}
                alt="User Avatar"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#6c757d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
            )}
          </div>
          <span style={{ fontWeight: 600, fontSize: "14px" }}>
            {user?.name?.split(" ")[0] || "User"}
          </span>
          <i
            className={`bi bi-chevron-${isOpen ? "up" : "down"}`}
            style={{ marginLeft: 4, fontSize: "12px" }}
          />
        </div>

        {isOpen && (
          <div
            ref={dropdownRefToUse}
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              backgroundColor: "#fff",
              padding: "8px 0",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 9999,
              minWidth: 150,
              color: "#111",
              marginTop: "4px",
              border: "1px solid rgba(0,0,0,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Link
              to="/profile"
              onClick={() => {
                setShowProfileDropdown(false);
                setShowMobileProfileDropdown(false);
                if (isMobile) {
                  setToggleOffCanvas(false);
                }
              }}
              style={{
                display: "block",
                padding: "8px 16px",
                textDecoration: "none",
                color: "#111",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              Dashboard
            </Link>
            <hr
              style={{
                margin: "4px 0",
                border: "none",
                borderTop: "1px solid #eee",
              }}
            />
            <button
              onClick={() => {
                handleLogout();
                if (isMobile) {
                  setToggleOffCanvas(false);
                }
              }}
              style={{
                border: "none",
                background: "none",
                padding: "8px 16px",
                color: "#dc3545",
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                transition: "background-color 0.2s",
                fontSize: "14px",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              Logout
            </button>
          </div>
        )}
      </div>
    );
  };

  // Show loading state briefly to prevent flash
  if (isLoading) {
    return (
      <section className="custom-header-section container-fluid">
        <div className="row">
          <div
            className="col-12 d-flex justify-content-center align-items-center"
            style={{ height: "70px" }}
          >
            <div style={{ color: "#fff" }}>Loading...</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="custom-header-section container-fluid">
      <div className="row position-relative">
        {/* Header Logo */}
        <div className="col-10 col-md-6 col-lg-4 py-2 ps-2 ps-md-3 ps-lg-3 pe-0 d-flex justify-content-start justify-content-lg-start align-items-center">
          <div className="custom-header-logo d-flex">
            <img
              src={HeaderLogo}
              onClick={scrollToTop}
              alt="Sorted Trips Logo"
              className="img-fluid"
              style={{ cursor: "pointer" }}
            />
          </div>
          <h3 className="custom-logo-text ms-2 mb-0 text-light">
            <Link
              to="/"
              onClick={handleLogoClick}
              className="custom-logo-text ms-2 mb-0 text-light"
              style={{ textDecoration: "none" }}
            >
              Sorted Trips | Sorted Hai
            </Link>
          </h3>
        </div>

        {/* Tablet View Menu */}
        <div className="custom-header-nav-mobile-menu col-12 d-block d-md-block d-lg-none">
          <ul className="custom-header-nav-menu d-flex justify-content-center justify-content-md-center justify-content-lg-center align-items-center flex-wrap flex-md-wrap w-100 gap-5 gap-lg-3 gap-xl-5 m-0 ps-0 py-3">
            <li className="custom-nav-links">
              <Link to="/">
                <AiFillHome className="fs-2" />
                <span className="d-none d-md-inline">Home</span>
              </Link>
            </li>
            <li className="custom-nav-links">
              <Link to="/about">
                <RiInformation2Fill className="fs-2" />
                <span className="d-none d-md-inline">About</span>
              </Link>
            </li>
            <li className="custom-nav-links">
              <Link to="/contactus">
                <BiSolidContact className="fs-2" />
                <span className="d-none d-md-inline">Contact Us</span>
              </Link>
            </li>
            <li className="custom-nav-links">
              <Link to="/alltrips">
                <FaMountainSun className="fs-2" />
                <span className="d-none d-md-inline">All Trips</span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="col-2 col-md-6 d-flex d-md-block d-lg-none justify-content-center align-items-center">
          <div className="custom-header-offcanvas-nav-menu">
            <FiMenu
              className="icon"
              style={{ fontSize: "2rem", cursor: "pointer", color: "#fff" }}
              onClick={handleToggleOffCanvasMenu}
            />
            <div
              className={`custom-offcanvas-overlay${
                toggleOffCanvas ? " active" : ""
              }`}
              onClick={handleToggleOffCanvasMenu}
            >
              <div
                className={`custom-offcanvas-nav${
                  toggleOffCanvas ? " active" : ""
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="custom-offcanvas-toogle-btn">
                  <ImCross
                    className="icon"
                    onClick={handleToggleOffCanvasMenu}
                    style={{ cursor: "pointer" }}
                  />
                </div>
                <ul className="custom-offcanvas-nav-links ps-0 mb-0">
                  <li className="custom-offcanvas-nav-link">
                    <Link to="/" onClick={() => setToggleOffCanvas(false)}>
                      <AiFillHome className="fs-4" />
                      <span>Home</span>
                    </Link>
                  </li>
                  <li className="custom-offcanvas-nav-link">
                    <Link to="/about" onClick={() => setToggleOffCanvas(false)}>
                      <RiInformation2Fill className="fs-4" />
                      <span>About</span>
                    </Link>
                  </li>
                  <li className="custom-offcanvas-nav-link ps-0">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        <FaSuitcaseRolling className="fs-4" />
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleServiceToggle();
                          }}
                          style={{ marginLeft: 8 }}
                        >
                          Service{" "}
                          <i
                            className={`bi bi-chevron-${
                              serviceToggle ? "up" : "down"
                            } px-1 py-2`}
                          />
                        </span>
                      </span>
                    </a>
                    {serviceToggle && (
                      <div className="custom-nav-links-dropdown">
                        <div>
                          <Link
                            to="/tripdetailsform"
                            onClick={() => {
                              setServiceToggle(false);
                              setToggleOffCanvas(false);
                            }}
                          >
                            Custom Trips / Family trips
                          </Link>
                        </div>
                        <div>
                          <Link
                            to="/weekendtrips"
                            onClick={() => {
                              setServiceToggle(false);
                              setToggleOffCanvas(false);
                            }}
                          >
                            Fixed Departures
                          </Link>
                        </div>
                        <div>
                          <Link
                            to="/specialtripsdropdown"
                            onClick={() => {
                              setServiceToggle(false);
                              setToggleOffCanvas(false);
                            }}
                          >
                            Special Trips
                          </Link>
                        </div>
                      </div>
                    )}
                  </li>
                  <li className="custom-offcanvas-nav-link">
                    <Link
                      to="/contactus"
                      onClick={() => setToggleOffCanvas(false)}
                    >
                      <BiSolidContact className="fs-4" />
                      <span>Contact</span>
                    </Link>
                  </li>
                  <li className="custom-offcanvas-nav-link">
                    <Link
                      to="/alltrips"
                      onClick={() => setToggleOffCanvas(false)}
                    >
                      <FaMountainSun className="fs-4" />
                      <span>All Trips</span>
                    </Link>
                  </li>
                  <li>
                    <div className="col-12 col-md-12 col-lg-1 custom-header-user-profile d-md-flex d-lg-flex align-items-center justify-content-center justify-content-md-center justify-content-md-center justify-content-lg-end px-3 px-lg-4 my-3 my-md-3">
                      {user ? (
                        <ProfileDropdown isMobile={true} />
                      ) : (
                        <a
                          className="d-flex p-2 me-1"
                          style={{ color: "white", textDecoration: "none" }}
                          href="#"
                          onClick={handleLoginToggle}
                        >
                          <span
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <BiLogIn className="custom-user-profile-icons me-2" />
                          </span>
                          <span className="pe-1 fs-6">Login/SignUp</span>
                        </a>
                      )}
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Navigation Menu */}
        <div className="col-12 col-md-7 col-lg-6 d-none d-md-none d-lg-flex justify-content-center align-items-center p-0 my-2 my-md-2">
          <ul className="custom-header-nav-menu">
            <li className="custom-nav-links">
              <Link to="/">
                <AiFillHome className="custom-nav-links-icons fs-4" />
                <span>Home</span>
              </Link>
            </li>
            <li className="custom-nav-links">
              <Link to="/about">
                <RiInformation2Fill className="custom-nav-links-icons fs-4" />
                <span>About</span>
              </Link>
            </li>
            <li
              className="custom-nav-links position-relative"
              onMouseEnter={() => setServiceToggle(true)}
              onMouseLeave={() => setServiceToggle(false)}
            >
              <a href="#" onClick={(e) => e.preventDefault()}>
                <FaSuitcaseRolling className="custom-nav-links-icons fs-4" />
                <span>
                  Service{" "}
                  <i
                    className={`bi bi-chevron-${
                      serviceToggle ? "up" : "down"
                    } px-1 py-2`}
                  />
                </span>
              </a>
              {serviceToggle && (
                <div className="custom-nav-links-dropdown">
                  <div>
                    <Link
                      to="/tripdetailsform"
                      onClick={() => setServiceToggle(false)}
                    >
                      Custom Trips / Family Trips
                    </Link>
                  </div>
                  <div>
                    <Link
                      to="/weekendtrips"
                      onClick={() => setServiceToggle(false)}
                    >
                      Fixed Departures
                    </Link>
                  </div>
                  <div>
                    <Link
                      to="/specialtripsdropdown"
                      onClick={() => setServiceToggle(false)}
                    >
                      Special Trips
                    </Link>
                  </div>
                </div>
              )}
            </li>
            <li className="custom-nav-links">
              <Link to="/contactus">
                <BiSolidContact className="custom-nav-links-icons fs-4" />
                <span>Contact</span>
              </Link>
            </li>
            <li className="custom-nav-links">
              <Link to="/alltrips">
                <FaMountainSun className="custom-nav-links-icons fs-4" />
                <span>All Trips</span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Desktop Login/Profile Section */}
        <div className="d-none d-md-none col-12 col-md-2 col-lg-2 custom-header-user-profile d-lg-flex align-items-center justify-content-center justify-content-md-center justify-content-lg-end px-3 px-lg-4 my-3 my-md-3">
          {user ? (
            <ProfileDropdown />
          ) : (
            <a
              className="d-flex p-2 me-1"
              style={{ color: "white", textDecoration: "none" }}
              href="#"
              onClick={handleLoginToggle}
            >
              <span
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <BiLogIn className="custom-user-profile-icons fs-4 me-2" />
              </span>
              <span className="pe-1 fs-6">Login/SignUp</span>
            </a>
          )}
        </div>
      </div>

      {/* Login Modal */}
      {loginToggle && (
        <div className="custom-header-login-toggle px-0">
          <Login
            loginToggle={loginToggle}
            setLoginToggle={setLoginToggle}
            onLoginSuccess={handleLoginSuccess}
          />
        </div>
      )}
    </section>
  );
};

export default Header;
